import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { supabase } from '@/lib/supabase';
import { mockCourses, courseCategories, courseLevels, type Course } from '@/mocks/courses';
import { mapDbCourse, formatPrice } from '@/lib/courses';

const learnHero =
  'https://readdy.ai/api/search-image?query=Modern%20African%20online%20learning%20platform%20interface%2C%20students%20watching%20video%20course%20on%20laptops%2C%20digital%20education%20environment%2C%20warm%20classroom%20lighting%2C%20clean%20minimal%20design%2C%20professional%20photography&width=1600&height=800&seq=learn-hero-01&orientation=landscape';

type SortKey = 'recent' | 'price-asc' | 'price-desc';
type PriceFilter = 'all' | 'free' | 'paid';

function LearnPage() {
  const [courses, setCourses] = useState<Course[]>(mockCourses);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [level, setLevel] = useState<string | null>(null);
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('all');
  const [sort, setSort] = useState<SortKey>('recent');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('courses')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (err) throw err;
      if (data && data.length > 0) {
        setCourses((data as unknown[]).map((row) => mapDbCourse(row as never)));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les formations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = courses.filter((course) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      course.title.toLowerCase().includes(q) ||
      course.instructor.toLowerCase().includes(q) ||
      course.description.toLowerCase().includes(q);
    const matchesCategory = !category || course.category === category;
    const matchesLevel = !level || course.level === level;
    const matchesPrice =
      priceFilter === 'all' ||
      (priceFilter === 'free' ? course.price === null || course.price === 0 : course.price !== null && course.price > 0);
    return matchesSearch && matchesCategory && matchesLevel && matchesPrice;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'price-asc') return (a.price ?? 0) - (b.price ?? 0);
    if (sort === 'price-desc') return (b.price ?? 0) - (a.price ?? 0);
    return b.postedAt.localeCompare(a.postedAt);
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={learnHero}
              alt="WorldApeiron Learn - formations en ligne"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-4">
                WorldApeiron Learn
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4 leading-tight">
                Apprenez. Progressez. Évoluez.
              </h1>
              <p className="text-base md:text-lg text-foreground-400 max-w-xl mb-8 leading-relaxed">
                Des formations en ligne pré-enregistrées en vidéo et tutoriels, créées par des
                experts. Payez une fois et accédez au contenu à vie, à votre rythme.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1 max-w-xl flex items-center bg-background-50 border border-background-300/60 rounded-full overflow-hidden focus-within:border-primary-400">
                  <div className="w-11 h-11 flex items-center justify-center shrink-0">
                    <i className="ri-search-line text-foreground-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher une formation, un sujet..."
                    className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 pr-4 outline-none"
                  />
                </div>
                <Link
                  to="/dashboard/courses/new"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-full hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-add-line text-lg" />
                  Devenir instructeur
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="learn" />
        </section>

        {/* Filters */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
            <span className="text-sm font-semibold text-foreground-400">
              {filtered.length} formation{filtered.length > 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <label htmlFor="learn-sort" className="text-sm text-foreground-400">Trier par</label>
              <select
                id="learn-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="bg-background-100 border border-background-300/60 rounded-md px-3 py-2 text-sm text-foreground-50 outline-none focus:border-primary-400"
              >
                <option value="recent">Plus récentes</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  category === null
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-primary-400/40'
                }`}
              >
                Toutes les catégories
              </button>
              {courseCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    category === cat
                      ? 'bg-primary-500 text-foreground-950'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-primary-400/40'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setLevel(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  level === null
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-foreground-300/60'
                }`}
              >
                Tous niveaux
              </button>
              {courseLevels.map((lv) => (
                <button
                  key={lv}
                  onClick={() => setLevel(lv)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    level === lv
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-foreground-300/60'
                  }`}
                >
                  {lv}
                </button>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setPriceFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  priceFilter === 'all'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                }`}
              >
                Tous les prix
              </button>
              <button
                onClick={() => setPriceFilter('free')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  priceFilter === 'free'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                }`}
              >
                Gratuites
              </button>
              <button
                onClick={() => setPriceFilter('paid')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  priceFilter === 'paid'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-accent-400/40'
                }`}
              >
                Payantes
              </button>
            </div>
          </div>
        </section>

        {/* Courses grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
                <i className="ri-graduation-cap-line text-3xl text-primary-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Aucune formation trouvée
              </h2>
              <p className="text-sm text-foreground-400 mb-6">
                Aucune formation ne correspond à vos critères. Élargissez votre recherche.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {sorted.map((course) => (
                <article
                  key={course.id}
                  className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-400/40 transition-all duration-300 flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={course.cover}
                      alt={`Formation ${course.title}`}
                      title={`${course.title} - ${course.category}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-50 whitespace-nowrap">
                        {course.category}
                      </span>
                    </div>
                    {course.price === null || course.price === 0 ? (
                      <span className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                        Gratuit
                      </span>
                    ) : null}
                  </div>

                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-400 whitespace-nowrap">
                        {course.level}
                      </span>
                      {course.durationHours !== null && (
                        <span className="text-xs text-foreground-400 flex items-center gap-1">
                          <i className="ri-time-line" />
                          {course.durationHours}h
                        </span>
                      )}
                    </div>

                    <Link to={`/learn/${course.id}`}>
                      <h2 className="font-heading font-semibold text-foreground-50 leading-snug hover:text-primary-400 transition-colors line-clamp-2">
                        {course.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-foreground-400 mt-1">{course.instructor}</p>

                    <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                      <span className="font-semibold text-primary-400 whitespace-nowrap">
                        {formatPrice(course.price)}
                      </span>
                      <Link
                        to={`/learn/${course.id}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-foreground-950 font-medium text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
                      >
                        Voir la formation
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100 border-t border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-3">
              Vous êtes expert dans votre domaine ?
            </h2>
            <p className="text-foreground-400 mb-8">
              Créez et vendez vos formations en ligne sur WorldApeiron Learn. Publiez vos cours
              vidéo et touchez des apprenants partout en Afrique.
            </p>
            <Link
              to="/dashboard/courses/new"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-circle-line text-lg" />
              Publier une formation
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default LearnPage;